import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { premiereConnexion } from '@/modules/admin/api';
import { useAuth } from './AuthContext';

type Mode = 'login' | 'premiere';

const inputClass =
  'h-12 w-full rounded-md border border-input bg-background px-3.5 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring';

export function Login() {
  const { session, signIn, isLoading } = useAuth();
  const location = useLocation();
  const [mode, setMode] = useState<Mode>('login');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        Chargement…
      </div>
    );
  }

  if (session) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  async function onSubmitLogin(e: FormEvent) {
    e.preventDefault();
    setErreur(null);
    setEnCours(true);
    const { error } = await signIn(email, password);
    setEnCours(false);
    if (error) setErreur(traduireErreurAuth(error));
  }

  async function onSubmitPremiere(e: FormEvent) {
    e.preventDefault();
    setErreur(null);
    if (password.length < 8) {
      setErreur('Le mot de passe doit faire au moins 8 caractères.');
      return;
    }
    if (password !== password2) {
      setErreur('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setEnCours(true);
    try {
      await premiereConnexion(email, password);
      // Compte créé : on connecte directement (la session déclenche la redirection).
      const { error } = await signIn(email, password);
      if (error) {
        setErreur(traduireErreurAuth(error));
        setEnCours(false);
      }
    } catch (err) {
      setErreur(err instanceof Error ? err.message : 'Échec de la création du compte.');
      setEnCours(false);
    }
  }

  function switchMode(m: Mode) {
    setMode(m);
    setErreur(null);
    setPassword('');
    setPassword2('');
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md overflow-hidden rounded-lg border border-t-4 border-t-action bg-card p-8">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <svg viewBox="0 0 32 32" aria-hidden="true" className="h-9 w-9">
              <path
                d="M6 18 L16 8 L26 18 L23 18 L23 26 L19 26 L19 20 L13 20 L13 26 L9 26 L9 18 Z"
                fill="currentColor"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">IMP TOITURE CRM</h1>
          <div className="mx-auto mt-2 h-1 w-12 rounded-full bg-action" />
          <p className="mt-1 text-sm text-muted-foreground">
            {mode === 'login'
              ? 'Connectez-vous à votre espace'
              : 'Première connexion — créez votre mot de passe'}
          </p>
        </div>

        {mode === 'login' ? (
          <form onSubmit={onSubmitLogin} className="space-y-4">
            <div>
              <label htmlFor="email" className="mb-1 block text-sm font-medium">
                Adresse email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                className={inputClass}
                placeholder="vous@imptoiture.com"
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-1 block text-sm font-medium">
                Mot de passe
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
                className={inputClass}
              />
            </div>
            {erreur ? (
              <div
                role="alert"
                className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
              >
                {erreur}
              </div>
            ) : null}
            <button
              type="submit"
              disabled={enCours}
              className="h-12 w-full rounded-md bg-action px-4 text-base font-semibold text-action-foreground hover:bg-action-hover focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            >
              {enCours ? 'Connexion…' : 'Se connecter'}
            </button>
          </form>
        ) : (
          <form onSubmit={onSubmitPremiere} className="space-y-4">
            <div>
              <label htmlFor="reg_email" className="mb-1 block text-sm font-medium">
                Adresse email
              </label>
              <input
                id="reg_email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                className={inputClass}
                placeholder="vous@imptoiture.com"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Utilisez l'email que votre administrateur a enregistré.
              </p>
            </div>
            <div>
              <label htmlFor="reg_pwd" className="mb-1 block text-sm font-medium">
                Choisir un mot de passe
              </label>
              <input
                id="reg_pwd"
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="reg_pwd2" className="mb-1 block text-sm font-medium">
                Confirmer le mot de passe
              </label>
              <input
                id="reg_pwd2"
                type="password"
                required
                autoComplete="new-password"
                value={password2}
                onChange={(e) => {
                  setPassword2(e.target.value);
                }}
                className={inputClass}
              />
            </div>
            {erreur ? (
              <div
                role="alert"
                className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
              >
                {erreur}
              </div>
            ) : null}
            <button
              type="submit"
              disabled={enCours}
              className="h-12 w-full rounded-md bg-action px-4 text-base font-semibold text-action-foreground hover:bg-action-hover focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            >
              {enCours ? 'Création…' : 'Créer mon compte'}
            </button>
          </form>
        )}

        <div className="mt-6 text-center text-sm">
          {mode === 'login' ? (
            <button
              type="button"
              onClick={() => {
                switchMode('premiere');
              }}
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              Première connexion ? Créer mon compte
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                switchMode('login');
              }}
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              ← J'ai déjà un compte
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function traduireErreurAuth(message: string): string {
  if (message.includes('Invalid login credentials')) return 'Email ou mot de passe incorrect.';
  if (message.includes('Email not confirmed')) return 'Email non confirmé.';
  if (message.includes('rate limit')) return 'Trop de tentatives — réessayez dans quelques minutes.';
  return message;
}
